import type { Permission } from '@/config/permissions';
import { usePermission } from '@/hooks/usePermission';
import type { DirectiveBinding } from 'vue';

interface ComplexPerm {
  any?: Permission[];
  all?: Permission[];
}

function parseSimple(value: string): Permission[] {
  const str = value.trim();
  if (str.startsWith('[') && str.endsWith(']')) {
    try {
      const parsed = JSON.parse(str.replace(/'/g, '"')) as unknown;
      if (Array.isArray(parsed)) return parsed as Permission[];
    } catch {
      /* ignore */
    }
  }
  return [str as Permission];
}

function normalize(value: unknown): {
  mode: 'list' | 'complex';
  list: Permission[];
  complex?: ComplexPerm;
} {
  if (Array.isArray(value)) return { mode: 'list', list: value as Permission[] };
  if (typeof value === 'object' && value !== null) {
    const v = value as ComplexPerm;
    return { mode: 'complex', list: [], complex: { any: v.any, all: v.all } };
  }
  if (typeof value === 'string') return { mode: 'list', list: parseSimple(value) };
  return { mode: 'list', list: [] };
}

function evaluate(el: HTMLElement, binding: DirectiveBinding<unknown>) {
  const { mode, list, complex } = normalize(binding.value);
  const { has, complex: complexCheck } = usePermission();
  let allowed = true;
  if (mode === 'list') {
    allowed = has(list); // has(list) = 任意命中
  } else if (complex && (complex.any || complex.all)) {
    allowed = complexCheck({ any: complex.any, all: complex.all });
  }
  if (!allowed) {
    if (el.remove) el.remove();
    else if (el.parentNode) el.parentNode.removeChild(el);
  }
}

const permissionDirective = {
  beforeMount(el: HTMLElement, binding: DirectiveBinding<unknown>) {
    evaluate(el, binding);
  },
  mounted(el: HTMLElement, binding: DirectiveBinding<unknown>) {
    evaluate(el, binding); // 确保父节点已存在再二次校验
  },
  updated(el: HTMLElement, binding: DirectiveBinding<unknown>) {
    evaluate(el, binding);
  },
};

export default permissionDirective;
